import type { HttpCore } from '../http';

/**
 * `POST /auth/login`, `POST /auth/callback` — NOT session-manager JSON
 * routes. Authentication moved to ZewstID (Keycloak) OIDC + PKCE in the
 * ZewstID cutover; session-manager's legacy `/auth/register|login|logout|
 * validate` routes were removed (`services/session-manager/src/index.ts`,
 * "Legacy ... routes were removed in the ZewstID cutover"). Web signs in via
 * `oidc-client-ts`/`react-oidc-context`'s browser redirect; the mobile plan
 * (`docs/eng/mobile-app-plan.md` M0.2/M3.1) does the equivalent with
 * `expo-auth-session` against a new `hauraki-mobile` PKCE client, exchanging
 * the authorization code directly with ZewstID's token endpoint — a
 * different origin from `baseUrl`.
 *
 * These two functions type that token-exchange contract so a caller (native
 * or web) has one typed shape to code against; `tokenEndpoint` must be
 * supplied because it is ZewstID's origin, not this client's `baseUrl`.
 * `skipAuth: true` because there is no access token yet at this point.
 */

export interface OidcTokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  token_type: string;
  expires_in: number;
}

export interface PkceCodeExchangeRequest {
  tokenEndpoint: string;
  clientId: string;
  code: string;
  codeVerifier: string;
  redirectUri: string;
}

export interface PkceRefreshRequest {
  tokenEndpoint: string;
  clientId: string;
  refreshToken: string;
}

function toFormBody(fields: Record<string, string>): string {
  return Object.entries(fields)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
}

export function createAuthModule(core: HttpCore) {
  return {
    /** Authorization-code + PKCE exchange against ZewstID's token endpoint. */
    login: (input: PkceCodeExchangeRequest) =>
      core.request<OidcTokenResponse>(input.tokenEndpoint, {
        method: 'POST',
        skipAuth: true,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: toFormBody({
          grant_type: 'authorization_code',
          client_id: input.clientId,
          code: input.code,
          code_verifier: input.codeVerifier,
          redirect_uri: input.redirectUri,
        }),
      }),
    /** Same token endpoint, `refresh_token` grant — the mobile equivalent of `refreshToken` in `ApiClientConfig`. */
    callback: (input: PkceRefreshRequest) =>
      core.request<OidcTokenResponse>(input.tokenEndpoint, {
        method: 'POST',
        skipAuth: true,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: toFormBody({
          grant_type: 'refresh_token',
          client_id: input.clientId,
          refresh_token: input.refreshToken,
        }),
      }),
    /** GET /auth/me — session-manager, real and live. */
    me: () => core.request<AuthMe>('/auth/me'),
    /**
     * POST /auth/refresh — best-effort. No such route exists on
     * session-manager today; web's `authApi.refresh()` calls it optimistically
     * and `useSessionExpiry` falls back to `me()` on a 404. Typed here so a
     * caller can keep that same fallback behavior.
     */
    refresh: () => core.request<{ token: string; user: AuthMe } | undefined>('/auth/refresh', { method: 'POST' }),
  };
}

export interface AuthMe {
  id: string;
  email: string;
  role: string;
  /** Reviewer all-access flag — `'all'` for reviewers, `'standard'` otherwise. */
  access?: 'all' | 'standard';
  [key: string]: unknown;
}

export type AuthModule = ReturnType<typeof createAuthModule>;
